declare let gtag: Function;

import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DossierService } from '../../services/dossier.service';
import { AuthService } from '../../services/auth.service';
import { Dossier, DossierStatus } from '../../models/dossier.model';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-dossier-list',
  templateUrl: './dossier-list.component.html',
  styleUrls: ['./dossier-list.component.css']
})
export class DossierListComponent implements OnInit {

  dossiers: Dossier[] = [];
  loading = false;
  error = '';

  // --- Filter / sort / pagination ---
  filterRef = '';
  filterStatus = '';
  filterDateFrom = '';
  filterDateTo = '';
  sortBy: 'created_at' | 'external_id' | 'status' = 'created_at';
  sortDir: 'asc' | 'desc' = 'desc';
  pageSize = 25;
  pageOffset = 0;
  total = 0;
  private filterDebounce?: ReturnType<typeof setTimeout>;

  get pageNumber(): number {
    return Math.floor(this.pageOffset / this.pageSize) + 1;
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  get hasPrevPage(): boolean {
    return this.pageOffset > 0;
  }

  get hasNextPage(): boolean {
    return this.pageOffset + this.pageSize < this.total;
  }

  /** Debounced so the ref-search box doesn't fire a request per keystroke. */
  onFilterInputChange(): void {
    clearTimeout(this.filterDebounce);
    this.filterDebounce = setTimeout(() => this.applyFilters(), 300);
  }

  applyFilters(): void {
    this.pageOffset = 0;
    this.loadDossiers();
  }

  setSort(column: 'created_at' | 'external_id' | 'status'): void {
    if (this.sortBy === column) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = column;
      this.sortDir = 'asc';
    }
    this.pageOffset = 0;
    this.loadDossiers();
  }

  prevPage(): void {
    if (this.hasPrevPage) {
      this.pageOffset = Math.max(0, this.pageOffset - this.pageSize);
      this.loadDossiers();
    }
  }

  nextPage(): void {
    if (this.hasNextPage) {
      this.pageOffset += this.pageSize;
      this.loadDossiers();
    }
  }

  showCreateForm = false;
  newExternalId = '';
  newProcessingMode = 'manual';
  newBatchMode = false;
  newBatchBackend = '';
  newBatchModel = '';
  newBatchDocumentType = '';
  newBatchSchemaSource: 'internal' | 'external' = 'internal';
  newBatchOutputFormat = 'json';
  creating = false;

  /** Help dialog visibility */
  showHelpDialog = false;

  /** Keypair generation dialog */
  showKeypairDialog = false;
  keypairPassword = '';
  keypairConfirm = '';
  showKeypairPassword = false;
  showKeypairConfirm = false;
  keypairGenerating = false;
  keypairError = '';
  private pendingCreate = false;

  /** Available extraction models — initialized from environment, optionally overridden by /models API */
  availableModels: string[] = environment.modelOptions;

  /** Available extraction backends from /config endpoint */
  availableBackends: string[] = [];

  /** Billing parameters for model price labels */
  billingMarkup = 0;
  billingTokenValueUsd = 0;

  /** Available document types for batch mode pre-selection */
  internalDocumentTypes: string[] = [];
  externalDocumentTypes: string[] = [];

  /** Available output formats for batch mode (loaded from /document_types) */
  availableOutputFormats: string[] = ['json', 'xml', 'csv'];

  /** Expose enum to template */
  readonly DossierStatus = DossierStatus;

  constructor(
    private dossierService: DossierService,
    private authService: AuthService,
    private translate: TranslateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadDossiers();
  }

  loadDossiers(): void {
    this.loading = true;
    this.error = '';
    this.dossierService.listDossiers({
      ref: this.filterRef.trim() || undefined,
      status: this.filterStatus || undefined,
      dateFrom: this.filterDateFrom || undefined,
      dateTo: this.filterDateTo || undefined,
      sortBy: this.sortBy,
      sortDir: this.sortDir,
      limit: this.pageSize,
      offset: this.pageOffset,
    }).subscribe({
      next: (data) => {
        this.dossiers = data.items;
        this.total = data.total;
        this.loading = false;
      },
      error: (err) => {
        this.error = err?.error?.detail || err?.message || 'Failed to load dossiers';
        this.loading = false;
      }
    });
  }

  navigateToDossier(dossier: Dossier): void {
    this.router.navigate(['/dossiers', dossier.oid]);
  }

  openCreateForm(): void {
    this.showCreateForm = true;
    this.newExternalId = '';
    this.newProcessingMode = 'manual';
    this.newBatchMode = false;
    this.newBatchBackend = '';
    this.newBatchModel = '';
    this.newBatchDocumentType = '';
    this.newBatchSchemaSource = 'internal';
    this.newBatchOutputFormat = 'json';
    this.loadModelsAndBackends();
  }

  cancelCreate(): void {
    this.showCreateForm = false;
  }

  /** Load available backends and document types for batch mode selection. */
  private loadModelsAndBackends(): void {
    this.dossierService.getConfig().subscribe({
      next: (config: Record<string, any>) => {
        const defaults = config['defaults'] || {};
        const billing = config['billing'] || {};
        // Build backend list from known backends
        this.availableBackends = ['openai', 'bedrock', 'ollama', 'local'];
        // Capture billing params for price labels
        this.billingMarkup = billing['markup'] || 0;
        this.billingTokenValueUsd = billing['token_value_usd'] || 0;
        // Pre-select defaults if available
        if (defaults['extract_backend']) {
          this.newBatchBackend = defaults['extract_backend'];
        }
        if (defaults['model']) {
          this.newBatchModel = defaults['model'];
        }
      },
      error: () => { this.availableBackends = ['openai', 'bedrock', 'ollama', 'local']; }
    });
    this.dossierService.getDocumentTypes().subscribe({
      next: (res) => {
        this.internalDocumentTypes = res.internal || [];
        this.externalDocumentTypes = res.external || [];
        // Load default output formats for batch mode selector
        const outputFormats = res.output_formats;
        if (outputFormats && outputFormats['default']) {
          this.availableOutputFormats = outputFormats['default'];
        }
      },
      error: () => {
        this.internalDocumentTypes = [];
        this.externalDocumentTypes = [];
      }
    });
  }

  onBatchModeChange(): void {
    if (!this.newBatchMode) {
      this.newBatchBackend = '';
      this.newBatchModel = '';
      this.newBatchDocumentType = '';
      this.newBatchSchemaSource = 'internal';
      this.newBatchOutputFormat = 'json';
    }
  }

  /** When backend changes, reset model to first available in that backend. */
  onBatchBackendChange(): void {
    const filtered = this.filteredBatchModels;
    if (filtered.length && !filtered.includes(this.newBatchModel)) {
      this.newBatchModel = filtered[0];
    } else if (!filtered.length) {
      this.newBatchModel = '';
    }
  }

  /** When schema source changes, reset document type selection. */
  onBatchSchemaSourceChange(): void {
    this.newBatchDocumentType = '';
  }

  /** Models filtered by the selected backend provider. */
  get filteredBatchModels(): string[] {
    switch (this.newBatchBackend) {
      case 'ollama':
        return this.availableModels.filter(m => m.startsWith('ollama/'));
      case 'bedrock':
        return this.availableModels.filter(m => m.startsWith('bedrock/'));
      case 'local':
        return this.availableModels.filter(m => m.startsWith('local/'));
      case 'openai':
        return this.availableModels.filter(m => !m.startsWith('ollama/') && !m.startsWith('bedrock/') && !m.startsWith('local/'));
      default:
        return this.availableModels;
    }
  }

  /** Strip provider prefix from model name for display. */
  displayModel(m: string): string {
    const slash = m.indexOf('/');
    return slash >= 0 ? m.substring(slash + 1) : m;
  }

  /** User-facing backend label (hides internal provider names). */
  displayBackend(b: string): string {
    const labels: Record<string, string> = { ollama: 'Local AI', openai: 'OpenAI', bedrock: 'Bedrock', local: 'vLLM' };
    return labels[b] || b;
  }

  /** Document types filtered by the selected schema source. */
  get filteredBatchDocumentTypes(): string[] {
    return this.newBatchSchemaSource === 'external'
      ? this.externalDocumentTypes
      : this.internalDocumentTypes;
  }

  /** Price label for a model (billing tokens per 1M LLM tokens). */
  modelPriceLabel(model: string): string {
    const p = (environment.modelPricing as any)[model];
    if (!p || !this.billingTokenValueUsd || !this.billingMarkup) return '';
    const inputRate = (p.input * this.billingMarkup) / this.billingTokenValueUsd;
    const outputRate = (p.output * this.billingMarkup) / this.billingTokenValueUsd;
    return `${inputRate.toFixed(0)}/${outputRate.toFixed(0)} ꝟ/1M`;
  }

  /** Batch mode requires model selection to be valid. */
  get isBatchValid(): boolean {
    if (!this.newBatchMode) { return true; }
    return !!this.newBatchModel.trim();
  }

  get canCreate(): boolean {
    return !!this.newExternalId.trim() && this.isBatchValid && !this.creating;
  }

  createDossier(): void {
    if (!this.canCreate) { return; }
    this.creating = true;
    // Prefix with xsd/ for external schema types
    const rawDocType = this.newBatchDocumentType.trim();
    const effectiveDocType = rawDocType
      ? (this.newBatchSchemaSource === 'external' ? 'xsd/' + rawDocType : rawDocType)
      : null;
    this.dossierService.createDossier(
      this.newExternalId.trim(),
      this.newProcessingMode,
      this.newBatchMode,
      effectiveDocType,
      this.newBatchModel.trim() || null,
      this.newBatchMode ? this.newBatchOutputFormat : null,
    ).subscribe({
      next: () => {
        this.showCreateForm = false;
        this.creating = false;
        this.loadDossiers();
        if (typeof gtag === 'function') {
          gtag('event', 'dossier_created', { processing_mode: this.newProcessingMode, batch_mode: this.newBatchMode });
        }
      },
      error: (err) => {
        const detail: string = err?.error?.detail || err?.message || '';
        if (detail.toLowerCase().includes('keypair')) {
          this.pendingCreate = true;
          this.showKeypairDialog = true;
          this.keypairPassword = '';
          this.keypairConfirm = '';
          this.keypairError = '';
        } else {
          this.error = detail || 'Failed to create dossier';
        }
        this.creating = false;
      }
    });
  }

  submitKeypair(): void {
    const minLen = 8;
    if (this.keypairPassword.length < minLen) {
      this.keypairError = this.translate.instant('dossier.keypairPasswordTooShort');
      return;
    }
    if (this.keypairPassword !== this.keypairConfirm) {
      this.keypairError = this.translate.instant('dossier.keypairPasswordMismatch');
      return;
    }
    this.keypairGenerating = true;
    this.keypairError = '';
    this.authService.createKeypair(this.keypairPassword).subscribe({
      next: () => {
        this.keypairGenerating = false;
        this.showKeypairDialog = false;
        if (this.pendingCreate) {
          this.pendingCreate = false;
          this.createDossier();
        }
      },
      error: (err) => {
        this.keypairError = err?.error?.detail || err?.message || 'Failed to generate keypair';
        this.keypairGenerating = false;
      }
    });
  }

  cancelKeypairDialog(): void {
    this.showKeypairDialog = false;
    this.pendingCreate = false;
    this.keypairPassword = '';
    this.keypairConfirm = '';
    this.keypairError = '';
  }

  openHelpDialog(): void {
    this.showHelpDialog = true;
  }

  closeHelpDialog(): void {
    this.showHelpDialog = false;
  }

  deleteDossier(event: Event, dossier: Dossier): void {
    event.stopPropagation();
    if (!confirm('Are you sure you want to delete this dossier?')) {
      return;
    }
    this.dossierService.deleteDossier(dossier.oid).subscribe({
      next: () => this.loadDossiers(),
      error: (err) => {
        this.error = err?.error?.detail || err?.message || 'Failed to delete dossier';
      }
    });
  }

  isProcessing(dossier: Dossier): boolean {
    return dossier.status === DossierStatus.PROCESSING;
  }

  getStatusColor(status: DossierStatus): string {
    switch (status) {
      case DossierStatus.CREATED:    return '#9e9e9e';
      case DossierStatus.PROCESSING: return '#1976d2';
      case DossierStatus.COMPLETED:  return '#4caf50';
      case DossierStatus.DELETED:    return '#f44336';
      default:                       return '#9e9e9e';
    }
  }
}
